import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Offcanvas, Dropdown } from 'react-bootstrap';
import GoogleComponent from '../pages/auth/Clientlogin/Google';

// Import Images
import logoWhite from '../assets/images/logo-white.svg'
import bannerBgImg from '../assets/images/landing-banner-bg-img.png'
import bannerBgMobileImg from '../assets/images/landing-banner-bg-mobile-img.png'
import custodianWalletImg from '../assets/images/custodian-wallet-img.svg'
import hardwareWalletImg from '../assets/images/hardware-wallet-img.svg'
import mobileWalletImg from '../assets/images/mobile-wallet-img.svg'
import isoLogoImg from '../assets/images/iso-logo-img.png'
import fiatLogoImg from '../assets/images/fiat-logo-img.png'
import securityImg from '../assets/images/security-img.png'
import footerBgImg from '../assets/images/footer-bg-img.png'
import footerBgMobileImg from '../assets/images/footer-bg-mobile-img.png'

// Import Icons
import offerIcon from "../assets/images/icons/offer-black-icon.svg";
import menuBarIcon from "../assets/images/icons/hamburger-menu-white.svg";

// Constants
const CRYPTO_SECRET_KEY = 'C@Ddoe^7@@8kkPRK'; // Move to environment variables in production
const PRODUCT_NAVIGATION_URLS = {
    custodian: '/home-page',
    'self-custodian': 'https://betaselfcustody.doru.co',
    'mobile-app': '#',
    hardware: '/xdc-landing'
};

// Wallet data configuration
const WALLET_CONFIGS = [
    {
        title: "Custodian Wallet",
        image: custodianWalletImg,
        features: [
            "Secure storage of major digital assets (BTC, ETH, XDC, etc.) in FIPS-certified HSM vaults.",
            "Multi-signature authorization policies and strict governance.",
            "Integrated compliance (AML/KYC, travel rule) for institutional use."
        ]
    },
    {
        title: "Air-gapped Hardware Wallet",
        image: hardwareWalletImg,
        features: [
            "100% offline cold storage device.",
            "Air-gapped, tamper-proof architecture for ultimate key security.",
            "Designed for institutions, funds, and HNIs who demand sovereign control over assets."
        ]
    },
    {
        title: "Offline Mobile Wallet",
        image: mobileWalletImg,
        features: [
            "Mobile-first, non-custodial wallet with offline signing capabilities.",
            "Works in disconnected or secure environments.",
            "Ideal for enterprises and high-net-worth users requiring mobility + security."
        ]
    }
];

// Trading desk configuration
const TRADING_CONFIGS = [
    {
        title: "ISO20022 Cross-Border Payments",
        image: isoLogoImg,
        features: [
            "Global fiat settlement rails with ISO20022 messaging.",
            "Any-currency-to-any-currency conversions.",
            "Compliant with international banking and payment standards."
        ]
    },
    {
        title: "OTC Trading Desk",
        image: fiatLogoImg,
        features: [
            "One-click fiat <> crypto and crypto <> crypto conversions.",
            "Deep liquidity, competitive execution, and no market slippage."
        ]
    }
];

// Compliance features
const COMPLIANCE_FEATURES = [
    "Offline, tamper-proof hardware wallets for cold storage.",
    "Offline mobile wallets for controlled enterprise use.",
    "Multi-signature controls & governance policies.",
    "FIPS-certified storage",
    "Full AML/KYC + ISO20022 alignment",
    "Third-party audits & governance control",
    "Offline key management"
];

// Custom hook for session management
const useSessionManagement = () => {
    const [sessionValue, setSessionValue] = useState(false);
    const [encryptedSessionValue, setEncryptedSessionValue] = useState('');

    const sessionuser = useMemo(() => {
        return sessionValue ? JSON.parse(sessionValue) : null;
    }, [sessionValue]);

    useEffect(() => {
        setSessionValue(sessionStorage.getItem("logedinUser"));
    }, []);

    useEffect(() => {
        if (sessionValue) {
            import('crypto-js').then(CryptoJS => {
                const ciphertext = CryptoJS.AES.encrypt(sessionValue, CRYPTO_SECRET_KEY).toString();
                setEncryptedSessionValue(encodeURIComponent(ciphertext));
            }).catch(err => console.error('CryptoJS import failed', err));
        }
    }, [sessionValue]);

    const logout = useCallback(() => {
        sessionStorage.removeItem('logedinUser');
        setSessionValue(false);
        window.location.reload();
    }, []);

    return { sessionuser, encryptedSessionValue, logout };
};

// Custom hook for authentication modals
const useAuthModals = () => {
    const [signupShow, setSignupShow] = useState(false);
    const [loginShow, setLoginShow] = useState(false);
    const [loginOffcanvas, setLoginOffcanvas] = useState(false);
    const [signupOffcanvas, setSignupOffcanvas] = useState(false);

    const authHandlers = useMemo(() => ({
        handleSignupClose: () => setSignupShow(false),
        handleSignupShow: () => {
            setSignupShow(true);
            setLoginShow(false);
        },
        handleLoginClose: () => setLoginShow(false),
        handleLoginShow: () => {
            setLoginShow(true);
            setSignupShow(false);
        },
        onSignupOffcanvasClose: () => setSignupOffcanvas(false),
        onSignupOffcanvasShow: () => {
            setSignupOffcanvas(true);
            setLoginOffcanvas(false);
        },
        onLoginOffcanvasClose: () => setLoginOffcanvas(false),
        onLoginOffcanvasShow: () => {
            setLoginOffcanvas(true);
            setSignupOffcanvas(false);
        }
    }), []);

    return {
        signupShow,
        loginShow,
        loginOffcanvas,
        signupOffcanvas,
        ...authHandlers
    };
};

// Reusable components
const WalletCard = ({ title, image, features }) => (
    <div className="wallet-card">
        <div className="row">
            <div className="col-md-6 align-self-center">
                <div className="content-wrapper">
                    <h3 className="f-32 w-500">{title}</h3>
                    <ul className="feature-list">
                        {features.map((feature, index) => (
                            <li key={index}>{feature}</li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="col-md-6 align-self-center">
                <div className="img-wrapper">
                    <img src={image} alt={title} />
                </div>
            </div>
        </div>
    </div>
);

const TradingCard = ({ title, image, features }) => (
    <div className="trading-card">
        <div className="content">
            <h3 className="f-24 w-500">
                {title.split(' ').map((word, index, array) => (
                    <React.Fragment key={index}>
                        {word}
                        {index === Math.floor(array.length / 2) - 1 && <br className="m-visible" />}
                        {index < array.length - 1 && ' '}
                    </React.Fragment>
                ))}
                <img src={image} alt={title} />
            </h3>
            <ul className="feature-list">
                {features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                ))}
            </ul>
        </div>
        <div className="icon">
            <img src={image} alt={title} />
        </div>
    </div>
);

const AuthModal = ({ show, onHide, title, onSwitchAuth, switchText, switchAction }) => (
    <Modal show={show} onHide={onHide} className="auth-modal" centered>
        <Modal.Body>
            <div className="auth-modal-wrapper google-only">
                <div className="auth-modal-center">
                    <div className="content-wrapper">
                        <h2>{title}</h2>
                        <div className="google-login-center">
                            <GoogleComponent />
                        </div>
                        <p className="switch-auth">
                            {switchText}
                            <a href="#" onClick={(e) => { e.preventDefault(); onHide(); onSwitchAuth(); }}>
                                {switchAction}
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </Modal.Body>
    </Modal>
);

const AuthOffcanvas = ({ show, onHide, title, onSwitchAuth, switchText, switchAction }) => (
    <Offcanvas show={show} onHide={onHide} placement="end" className="auth-offcanvas">
        <Offcanvas.Header closeButton>
            <Offcanvas.Title>{title}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
            <div className="auth-content google-only-offcanvas">
                <div className="content-wrapper">
                    <div className="google-login-center">
                        <GoogleComponent />
                    </div>
                    <p className="switch-auth">
                        {switchText}
                        <a href="#" onClick={(e) => { e.preventDefault(); onHide(); onSwitchAuth(); }}>
                            {switchAction}
                        </a>
                    </p>
                </div>
            </div>
        </Offcanvas.Body>
    </Offcanvas>
);

function LandingPage() {
    // State management
    const [openMenu, setOpenMenu] = useState(false);
    const [showProductsDropdown, setShowProductsDropdown] = useState(false);

    // Custom hooks
    const { sessionuser, encryptedSessionValue, logout } = useSessionManagement();
    const authModals = useAuthModals();

    // Body class effect
    useEffect(() => {
        document.body.classList.add("landing-page-body");
        return () => document.body.classList.remove("landing-page-body");
    }, []);

    // Event handlers
    const toggleMenu = useCallback(() => {
        setOpenMenu(prev => !prev);
    }, []);

    const toggleProductsDropdown = useCallback(() => {
        setShowProductsDropdown(prev => !prev);
    }, []);

    const handleProductNavigation = useCallback((type) => {
        if (!sessionuser) return;

        const url = PRODUCT_NAVIGATION_URLS[type];
        if (type === 'self-custodian' && encryptedSessionValue) {
            window.open(`${url}?e=${encryptedSessionValue}`, '_blank');
        } else {
            window.location.href = url;
        }
        setShowProductsDropdown(false);
    }, [sessionuser, encryptedSessionValue]);

    const handleLinkClick = useCallback((e, action) => {
        e.preventDefault();
        action();
    }, []);

    return (
        <div className='home-page'>
            {/* Header */}
            <div className={`landing-header ${openMenu ? 'menu-open' : ''}`}>
                <div className="header-wrapper">
                    <div className="logo-wrapper">
                        <a href="#" className="logo">
                            <img src={logoWhite} alt="logo" />
                        </a>
                    </div>
                    <div className="navbar-menu">
                        <div className="menu-bar">
                            <img src={menuBarIcon} onClick={toggleMenu} alt="icon" />
                        </div>
                        <ul className={`menu ${openMenu ? 'active' : ''}`}>
                            <li className={`arrow dropdown ${showProductsDropdown ? 'active' : ''} `}>
                                <a href="#" onClick={(e) => handleLinkClick(e, () => toggleProductsDropdown())}>
                                    Products
                                </a>

                                <ul className={`dropdown-menu ${showProductsDropdown ? 'show' : ''}`}>
                                    <li><a href="#" onClick={(e) => sessionuser && handleLinkClick(e, () => handleProductNavigation('custodian'))}>Custodian</a></li>
                                    <li><a href="#" onClick={(e) => sessionuser && handleLinkClick(e, () => handleProductNavigation('self-custodian'))}>Self Custodian</a></li>
                                    <li><a href="#" onClick={(e) => sessionuser && handleLinkClick(e, () => handleProductNavigation('mobile-app'))}>Mobile App</a></li>
                                    <li><a href="#" onClick={(e) => sessionuser && handleLinkClick(e, () => handleProductNavigation('hardware'))}>Hardware</a></li>
                                </ul>

                            </li>
                            <li><a href="#">Contact Us</a></li>
                            {sessionuser ? (
                                <li>
                                    <div className="dropdown-v1">
                                        <Dropdown>
                                            <Dropdown.Toggle>
                                                <p>{sessionuser.username}</p>
                                                <img width="50px" height="50px" src={sessionuser.picture} alt="User profile" />
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item href="/" onClick={logout}>Log out</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                </li>
                            ) : (
                                <>
                                    <li><a href="#" onClick={(e) => handleLinkClick(e, authModals.onLoginOffcanvasShow)}>Log In</a></li>
                                    <li><a href="#" onClick={(e) => handleLinkClick(e, authModals.onSignupOffcanvasShow)}>Sign Up</a></li>
                                </>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Banner Section */}
            <section className="sec-landing-banner">
                <div className="container">
                    <div className="banner-wrapper">
                        <img src={bannerBgImg} alt="Banner background" className="bg-img d-none d-md-block" />
                        <img src={bannerBgMobileImg} alt="Mobile banner background" className="bg-img d-block d-md-none" />
                        <div className="banner-content">
                            <div className="banner-tag">
                                <img src={offerIcon} alt="Offer icon" />
                                <p>We're offering zero fees for early users for a limited time - Apply now.</p>
                            </div>
                            <h1 className="d-none d-md-block">The ultimate Security for <br />
                                your digital assets</h1>
                            <h1 className="d-block d-md-none">The ultimate<br />
                                Security for your<br />
                                digital assets</h1>
                            <h3>We provide one trusted platform for secure storage, cold storage, air-gapped devices, <br />
                                and institutional-grade custody, for every level of digital asset security.</h3>
                            <ul className="btn-list">
                                <li><a href="#" className="btn-style transparent">Join Early Access Program</a></li>

                                {!sessionuser ? (<li><a href="#" className="btn-style transparent" onClick={(e) => handleLinkClick(e, authModals.onLoginOffcanvasShow)}>Log In</a></li>) : ""}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Hybrid Setup Section */}
            <section className="sec-hubrid-setup">
                <div className="container p-lr-40">
                    <div className="content-wrapper">
                        <h2 className="f-48 w-400"><b>Our hybrid setup combines</b> bank-level custody <br />
                            <b>with air-gapped devices and mobile wallets for</b> <br />
                            Funds, Fintechs, Enterprise, and individuals.</h2>
                    </div>
                </div>
            </section>

            {/* Digital Asset Section */}
            <section className="sec-digital-asset">
                <div className="container p-lr-40">
                    <div className="content-wrapper">
                        <h2 className="f-48 w-400">Your Digital Asset <br className="m-visible" /> Challenges, Solved.</h2>
                        <h3 className="w-400">Doru integrates crypto custody, 100% air-gapped hardware wallets, offline mobile wallets, ISO20022 <br /> cross-border payments, and fiat/crypto OTC trading into a single institutional platform.</h3>
                    </div>
                </div>
            </section>

            {/* Wallet Section */}
            <section className="sec-mobile-wallet">
                <div className="container">
                    {WALLET_CONFIGS.map((wallet, index) => (
                        <WalletCard key={index} {...wallet} />
                    ))}
                </div>
            </section>

            {/* Trading Desk Section */}
            <section className="sec-trading-desk">
                <div className="container">
                    <div className="trading-grid">
                        {TRADING_CONFIGS.map((trading, index) => (
                            <TradingCard key={index} {...trading} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Security Section */}
            <section className="sec-security">
                <div className="container">
                    <div className="security-grid">
                        <div className="content">
                            <h3 className="w-500">Our ecosystem supports all major cryptocurrencies (BTC, ETH, XDC, etc.) and multiple fiat currencies, enabling seamless conversion between assets while ensuring enterprise-grade compliance and security.</h3>
                        </div>
                        <div className="img-wrapper">
                            <img src={securityImg} alt="Security illustration" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Compliance Section */}
            <section className="sec-compliance">
                <div className="container">
                    <div className="compliance-wrapper">
                        <h2 className="f-48 w-400">Security & Compliance</h2>
                        <h3 className="w-500">DORU.CO is built on multi-layered security, FIPS-certified HSMs & air-gapped vaults for custody.</h3>
                        <ul className="feature-list">
                            {COMPLIANCE_FEATURES.map((feature, index) => (
                                <li key={index}>
                                    <div className="feature-card">
                                        <h3>{feature}</h3>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Partnerships Section */}
            <section className="sec-partnerships">
                <div className="partnerships-wrapper">
                    <div className="container-fluid">
                        <div className="partnerships-grid">
                            <div className="partnerships-card">
                                <div className="content-wrapper">
                                    <h3 className="f-24 w-500">Global Banking & Payments </h3>
                                    <p>Partnerships with licensed banks and payment providers.</p>
                                </div>
                            </div>
                            <div className="partnerships-card">
                                <div className="content-wrapper">
                                    <h3 className="f-24 w-500">Blockchain Integrations</h3>
                                    <p>Support for leading Layer-1s and stablecoin issuers (BTC, ETH, XDC, etc.)</p>
                                </div>
                            </div>
                            <div className="partnerships-card">
                                <div className="content-wrapper">
                                    <h2 className="f-48 w-400">Strategic Partnerships</h2>
                                    <p className="black">Industry Collaborations – Working with funds, exchanges, and networks to co-develop custody + offline solutions.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Join Section */}
            <section className="sec-join">
                <div className="container">
                    <div className="content-wrapper">
                        <h2 className="f-48 w-400">Join Early Access and Go <br className="m-visible" /> Zero-Fee.</h2>
                        <p className="p">Get full access to our complete institutional platform, including secure custody, hardware wallets, <br /> and OTC trading with zero transaction fees for a limited time.</p>
                        <a href="https://docs.google.com/forms/d/e/1FAIpQLSfkq2Ee_T6re7jFLQpCTH52Cr5Q2Lzgq-lUc6YKyAw7tuhRNw/viewform" className="btn-style white">Apply for Zero-Fee Early Access</a>
                    </div>
                </div>
            </section>

            {/* Meeting Section */}
            <section className="sec-meeting">
                <div className="partnerships-wrapper">
                    <div className="container-fluid">
                        <div className="meeting-grid">
                            <div className="meeting-card">
                                <div className="content-wrapper">
                                    <h2 className="f-48 w-400">Institutional Collaboration</h2>
                                    <p>Be among the first to integrate with DORU.CO</p>
                                    <p>Explore partnerships across custody, offline hardware, mobile <br /> solutions, and payments</p>
                                </div>
                            </div>
                            <div className="meeting-card">
                                <div className="content-wrapper">
                                    <a href="#" className="f-24">Schedule a <br /> Demo →</a>
                                </div>
                            </div>
                            <div className="meeting-card">
                                <div className="content-wrapper">
                                    <a href="#" className="f-24">Join the Early <br /> Adopter program →</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <div className="landing-footer">
                <img src={footerBgImg} alt="Footer background" className="bg-img d-none d-md-block" />
                <img src={footerBgMobileImg} alt="Mobile footer background" className="bg-img d-block d-md-none" />
                <div className="container">
                    <div className="content-wrapper">
                        <p className="p">Disclaimer: Digital assets are risky and may lose value. DORU <br className="m-visible" /> does not provide any guarantee on its products or services.</p>
                    </div>
                </div>
            </div>

            <div className="landing-bottom-footer">
                <div className="container">
                    <div className="content-wrapper">
                        <p className="p">Products developed in the XDC Innovation Lab.</p>
                    </div>
                </div>
            </div>

            {/* Authentication Modals */}
            <AuthModal
                show={authModals.signupShow}
                onHide={authModals.handleSignupClose}
                title="Create Account"
                onSwitchAuth={authModals.handleLoginShow}
                switchText="Already have an account?"
                switchAction="Sign In"
            />

            <AuthModal
                show={authModals.loginShow}
                onHide={authModals.handleLoginClose}
                title="Welcome Back"
                onSwitchAuth={authModals.handleSignupShow}
                switchText="Don't have an account?"
                switchAction="Create Account"
            />

            {/* Authentication Offcanvas */}
            <AuthOffcanvas
                show={authModals.signupOffcanvas}
                onHide={authModals.onSignupOffcanvasClose}
                title="Create Account"
                onSwitchAuth={authModals.onLoginOffcanvasShow}
                switchText="Already have an account?"
                switchAction="Sign In"
            />

            <AuthOffcanvas
                show={authModals.loginOffcanvas}
                onHide={authModals.onLoginOffcanvasClose}
                title="Welcome Back"
                onSwitchAuth={authModals.onSignupOffcanvasShow}
                switchText="Don't have an account?"
                switchAction="Create Account"
            />
        </div>
    );
}

export default LandingPage;
